import { useState } from 'react';

import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useAdminGetWeightUnities } from '../../hooks/admin-get-weight-unities.hook';
import { useCreateWeightUnity } from '../../hooks/create-weight-unity.hook';
import { useUpdateWeightUnity } from '../../hooks/update-weight-unity.hook';
import { WeightUnity } from '../../types/weight-unity.type';
import { CreateWeightUnityRequestDTO } from '../../types/dtos/create-weight-unity.dto';
import { UpdateWeightUnityRequestDTO } from '../../types/dtos/update-weight-unity.dto';
import { WeightUnitiesTable } from '../../components/Tables/WeightUnitiesTable.component';
import { WeightUnityDialog } from '../../components/Modals/WeightUnityDialog.component';
import { CreateWeightUnityCard } from '../../components/Forms/CreateWeightUnityCard.component';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },

    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  listColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
  },
}));

export default function AdminWeightUnitiesListView() {
  const classes = useStyles();

  const { isLoading, isRefetching, weightUnities, refetch } = useAdminGetWeightUnities();
  const { isSubmitting: isCreating, createWeightUnity } = useCreateWeightUnity();
  const { isSubmitting: isUpdating, updateWeightUnity } = useUpdateWeightUnity();

  const [editedWeightUnity, setEditedWeightUnity] = useState<WeightUnity | null>(null);

  const closeDialog = () => setEditedWeightUnity(null);

  const handleCreate = async (weightUnityData: CreateWeightUnityRequestDTO): Promise<boolean> => {
    const createdWeightUnity = await createWeightUnity(weightUnityData);
    if (!createdWeightUnity) return false;

    await refetch();
    return true;
  };

  const handleEditSubmit = async (weightUnityData: UpdateWeightUnityRequestDTO) => {
    if (!editedWeightUnity) return;

    const updatedWeightUnity = await updateWeightUnity(editedWeightUnity.id, weightUnityData);
    if (!updatedWeightUnity) return;

    closeDialog();
    await refetch();
  };

  return (
    <div className={classes.root}>
      <h2>Unités de poids</h2>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8} className={classes.listColumn}>
          <WeightUnitiesTable
            weightUnities={weightUnities}
            isLoading={isLoading || isRefetching}
            onEdit={setEditedWeightUnity}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <h4>Créer une unité</h4>
          <CreateWeightUnityCard isSubmitting={isCreating} onCreate={handleCreate} />
        </Grid>
      </Grid>
      {editedWeightUnity && (
        <WeightUnityDialog
          open
          weightUnity={editedWeightUnity}
          isSubmitting={isUpdating}
          onSubmit={handleEditSubmit}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
